import { useEffect } from 'react';

interface AlertDialogProps {
  isOpen: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  onClose: () => void;
}

export default function AlertDialog({ isOpen, type, title, message, onClose }: AlertDialogProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
      data-testid="alert-dialog-overlay"
    >
      <div 
        className={`max-w-md w-full mx-4 p-6 rounded-lg shadow-xl border-2 ${colors[type]}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="alert-dialog"
        role="alertdialog"
        aria-labelledby="alert-title"
        aria-describedby="alert-message"
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl">{icons[type]}</span>
          <div className="flex-1">
            {title && (
              <h3 id="alert-title" className="font-semibold text-lg mb-2">
                {title}
              </h3>
            )}
            <p id="alert-message" className="text-sm whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
            data-testid="alert-dialog-close"
            autoFocus
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
}
